import { useMemo } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  useReducedMotion,
  AnimatePresence,
} from "framer-motion";
import { ArrowDown } from "lucide-react";
import LanternScene from "@/components/three/LanternScene";
import CelebrationButton from "@/components/ui/CelebrationButton";
import { useServiceTheme } from "@/context/ServiceThemeContext";
import { WHATSAPP_URL } from "@/data/services";
import { scrollToId } from "@/hooks/useLenis";

const LINES = ["Setiap Momen", "Layak Dirayakan", "Secara Digital"];

export default function Hero() {
  const { accent, glow } = useServiceTheme();
  const reduced = useReducedMotion();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 55, damping: 18 });
  const sy = useSpring(my, { stiffness: 55, damping: 18 });
  const backX = useTransform(sx, (v) => v * -14);
  const backY = useTransform(sy, (v) => v * -10);
  const midX = useTransform(sx, (v) => v * -30);
  const midY = useTransform(sy, (v) => v * -20);

  const bokeh = useMemo(
    () =>
      Array.from({ length: 14 }, (_, i) => ({
        left: `${(i * 61) % 100}%`,
        top: `${(i * 29 + 7) % 100}%`,
        size: 4 + (i % 5) * 5,
        opacity: 0.1 + (i % 4) * 0.05,
      })),
    []
  );

  const onMove = (e) => {
    if (reduced) return;
    mx.set(e.clientX / window.innerWidth - 0.5);
    my.set(e.clientY / window.innerHeight - 0.5);
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      onMouseMove={onMove}
      className="relative flex min-h-screen flex-col overflow-hidden bg-[#0B132B]"
    >
      <AnimatePresence mode="sync">
        <motion.div
          key={glow}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8 }}
          className="absolute inset-0"
          style={{
            background: `radial-gradient(circle at 68% 42%, ${glow}, transparent 60%)`,
          }}
        />
      </AnimatePresence>

      {/* Layer belakang: bokeh bintang (paling lambat) */}
      <motion.div aria-hidden className="absolute inset-0" style={{ x: backX, y: backY }}>
        {bokeh.map((b, i) => (
          <span
            key={i}
            className="absolute rounded-full bg-[#E8C98A]"
            style={{
              left: b.left,
              top: b.top,
              width: b.size,
              height: b.size,
              opacity: b.opacity,
              filter: "blur(1px)",
            }}
          />
        ))}
      </motion.div>

      {/* Layer tengah: motif geometris islami (medium) */}
      <motion.div
        aria-hidden
        className="absolute -right-24 top-1/2 w-[46rem] -translate-y-1/2 opacity-[0.13] md:opacity-20"
        style={{ x: midX, y: midY }}
      >
        <svg viewBox="0 0 200 200" className="h-auto w-full">
          <g fill="none" stroke="#C9A227" strokeWidth="0.5">
            <rect x="50" y="50" width="100" height="100" />
            <rect
              x="50"
              y="50"
              width="100"
              height="100"
              transform="rotate(45 100 100)"
            />
            <circle cx="100" cy="100" r="72" />
            <circle cx="100" cy="100" r="36" />
          </g>
        </svg>
      </motion.div>

      {/* Layer depan: lentera 3D (paling responsif) */}
      <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-full opacity-40 sm:opacity-60 lg:w-[52%] lg:opacity-100">
        <LanternScene accent={accent} />
      </div>

      <div className="relative z-20 mx-auto flex w-full max-w-7xl flex-1 flex-col justify-center px-6 pb-28 pt-36 lg:px-10">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-8 font-mono text-[11px] tracking-[0.4em] text-[#C9A227]"
          data-testid="hero-eyebrow"
        >
          احتفال — STUDIO PERAYAAN DIGITAL
        </motion.p>
        <h1
          className="font-display max-w-3xl text-5xl leading-[1.04] text-[#F6F0E6] sm:text-6xl lg:text-7xl xl:text-[5.2rem]"
          data-testid="hero-headline"
        >
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 1 ? "italic text-[#E8C98A]" : ""}`}
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{
                  duration: 0.9,
                  delay: 0.25 + i * 0.14,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.85 }}
          className="mt-8 max-w-xl text-base leading-relaxed text-[#F6F0E6]/70 md:text-lg"
          data-testid="hero-subcopy"
        >
          Dari undangan pernikahan sampai portal masjid — lima jenis website
          untuk merayakan babak penting hidupmu. Hangat, bercahaya, dan hidup.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <CelebrationButton
            variant="gold"
            href={WHATSAPP_URL}
            testId="hero-cta-primary"
            data-magnetic
            className="px-8 py-4 text-base"
          >
            Mulai Rayakan Momenmu
          </CelebrationButton>
          <CelebrationButton
            variant="ghost"
            href="#layanan"
            onClick={(e) => {
              e.preventDefault();
              scrollToId("#layanan");
            }}
            testId="hero-cta-secondary"
            data-magnetic
            className="px-8 py-4 text-base"
          >
            Jelajahi 5 Layanan
          </CelebrationButton>
        </motion.div>
      </div>

      <motion.button
        onClick={() => scrollToId("#layanan")}
        aria-label="Gulir ke bawah"
        data-testid="hero-scroll-cue"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 left-6 z-20 flex items-center gap-3 text-[#F6F0E6]/50 transition-colors hover:text-[#E8C98A] lg:left-10"
      >
        <motion.span
          animate={reduced ? {} : { y: [0, 6, 0] }}
          transition={{ repeat: Infinity, duration: 1.8 }}
        >
          <ArrowDown size={16} />
        </motion.span>
        <span className="font-mono text-[10px] tracking-[0.3em]">
          GULIR UNTUK MERAYAKAN
        </span>
      </motion.button>
    </section>
  );
}
